import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef } from "react";

import { getUsage } from "../../lib/api";
import { currentPageHref, withBillingReturn } from "../../lib/billing";
import { errorMessage } from "../../lib/errors";
import { formatPoints } from "../../lib/format";
import {
  formatBytes,
  formatPercent,
  quotaBarWidth,
  quotaPercent,
  quotaTone,
  usageOverLimit,
} from "../../lib/m4";
import { ErrorState, LoadingState } from "../states";

export interface UsageDialogProps {
  open: boolean;
  onClose: () => void;
  /** 购买页绝对地址；未连接计费端时为 null（升级按钮不可用） */
  purchaseUrl: string | null;
  /** 清理媒体：页面负责弹提示（这里不真删） */
  onCleanMedia: () => void;
}

function QuotaBar({
  label,
  usedLabel,
  limitLabel,
  used,
  limit,
}: {
  label: string;
  usedLabel: string;
  limitLabel: string;
  used: number;
  limit: number;
}) {
  const percent = quotaPercent(used, limit);
  const width = quotaBarWidth(used, limit);
  const tone = quotaTone(used, limit);
  const over = tone === "over";

  return (
    <div className="dp-quota" data-over={over ? "true" : undefined} data-tone={tone}>
      <div className="dp-quota-head">
        <span className="dp-quota-label">{label}</span>
        <span className="dp-quota-num">
          {usedLabel} <em>/</em> {limitLabel}
        </span>
        <b className="dp-quota-pct">{formatPercent(percent)}</b>
      </div>
      <div
        className="dp-quota-bar"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={width}
        aria-valuetext={`${formatPercent(percent)}，已用 ${usedLabel} / 上限 ${limitLabel}`}
      >
        <i style={{ width: `${width}%` }} aria-hidden="true" />
      </div>
      {over ? (
        <p className="dp-quota-over">已超出配额 {formatPercent(percent)}，写操作会被前置拦截。</p>
      ) : null}
    </div>
  );
}

/**
 * 用量弹层（左轨「本集已用」点开）。
 *
 * 数据全部来自 `GET /api/usage`；超限用 `--red`（视觉规范里失败色），
 * 并给出「清理媒体 / 升级套餐」引导；升级按钮直接打开计费端的购买页（后端拼好绝对地址）。
 */
/** 秒 → 「3 分 12 秒」；小于 1 分钟显示秒 */
export function formatRuntime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total} 秒`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return rest === 0 ? `${minutes} 分` : `${minutes} 分 ${rest} 秒`;
}

export function UsageDialog({ open, onClose, purchaseUrl, onCleanMedia }: UsageDialogProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const usageQuery = useQuery({
    queryKey: ["usage"],
    queryFn: () => getUsage(),
    enabled: open,
    staleTime: 15_000,
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const timer = setTimeout(() => closeRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      restoreRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const usage = usageQuery.data;
  const over = usage ? usageOverLimit(usage) : false;
  const projectsOver = usage ? usage.projects.used > usage.projects.limit : false;

  return (
    <div className="dp-scrim" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className="dp-dialog dp-usage"
      >
        <div className="dp-usage-head">
          <div>
            <p className="dp-label">usage · quota</p>
            <h2 id={titleId} className="dp-usage-title">
              用量与配额
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="dp-icon-btn"
            aria-label="关闭用量"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {usageQuery.isPending ? (
          <LoadingState label="正在读取用量…" />
        ) : usageQuery.isError || !usage ? (
          <ErrorState
            title="用量加载失败"
            message={errorMessage(usageQuery.error, "用量加载失败")}
            onRetry={() => void usageQuery.refetch()}
          />
        ) : (
          <>
            {over ? (
              <p role="alert" className="dp-usage-alert">
                {projectsOver && usage.projects.used > usage.projects.limit
                  ? "项目数已达上限，新建项目会被拦截（403 quota_exceeded）。"
                  : "媒体配额已超限，上传与生成会被前置拦截（403 quota_exceeded）。"}
                建议先清理素材，或升级套餐扩容。
              </p>
            ) : null}

            <div className="dp-usage-bars">
              <QuotaBar
                label="媒体配额"
                used={usage.media.usedBytes}
                limit={usage.media.limitBytes}
                usedLabel={formatBytes(usage.media.usedBytes)}
                limitLabel={formatBytes(usage.media.limitBytes)}
              />

              <QuotaBar
                label="项目数"
                used={usage.projects.used}
                limit={usage.projects.limit}
                usedLabel={`${usage.projects.used} 个`}
                limitLabel={`${usage.projects.limit} 个`}
              />
            </div>

            <div className="dp-usage-kv">
              <span>点数余额</span>
              <b>{formatPoints(usage.points.balance)} 点</b>
            </div>
            <div className="dp-usage-kv">
              <span>本周期已用点数</span>
              <b>{formatPoints(usage.points.used)} 点</b>
            </div>

            <table className="dp-data-table dp-usage-table">
              <caption className="dp-usage-caption">逐项目明细</caption>
              <thead>
                <tr>
                  <th>项目</th>
                  <th style={{ textAlign: "right" }}>媒体</th>
                  <th style={{ textAlign: "right" }}>点数</th>
                </tr>
              </thead>
              <tbody>
                {usage.byProject.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="dp-usage-empty">
                      还没有项目
                    </td>
                  </tr>
                ) : (
                  usage.byProject.map((row) => (
                    <tr key={row.projectId}>
                      <td>
                        <span className="dp-usage-project">{row.title}</span>
                        <span className="dp-usage-pid">{row.projectId}</span>
                      </td>
                      <td className="num">{formatBytes(row.mediaBytes)}</td>
                      <td className="num">{formatPoints(row.points)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {usage.colab && usage.colab.sessions.length > 0 ? (
              <section className="dp-colab-usage" aria-label="Colab 运行时长">
                <header className="dp-colab-head">
                  <h4>Colab 运行时长</h4>
                  <span className="dp-colab-tag">扣的是你 Colab 账号的 CU</span>
                </header>
                <p className="dp-colab-note">
                  这部分<b>不扣 Drama Pie 点数</b>：H3 出片跑在你自己的 Google Colab 上。
                  Colab 不提供按任务查询用量的接口，所以下面是实际开机时长，以及按机型单价<b>估算</b>的 CU。
                </p>
                <dl className="dp-colab-grid">
                  <div>
                    <dt>运行时长</dt>
                    <dd>{formatRuntime(usage.colab.totalRuntimeSeconds)}</dd>
                  </div>
                  <div>
                    <dt>估算用量</dt>
                    <dd>
                      约 {usage.colab.estimatedCu.toFixed(2)} CU
                      <em>≈ ¥{usage.colab.estimatedCny.toFixed(2)}</em>
                    </dd>
                  </div>
                  <div>
                    <dt>机器</dt>
                    <dd>{usage.colab.sessions.length} 台 · {usage.colab.pricingBasis.gpu}</dd>
                  </div>
                </dl>
                <p className="dp-colab-basis">{usage.colab.pricingBasis.note}</p>
              </section>
            ) : null}

            <div className={`dp-usage-guide${over ? " is-over" : ""}`}>
              <p>
                额度是硬上限：媒体超限时上传/生成会在写入前被 403 拦下，不会留下半个对象。
              </p>
              <div className="dp-usage-guide-actions">
                <button type="button" className="dp-btn sm" onClick={onCleanMedia}>
                  清理媒体
                </button>
                <button
                  type="button"
                  className="dp-btn sm primary"
                  disabled={!purchaseUrl}
                  title={purchaseUrl ? "在新标签页打开购买页" : "未连接计费账户，暂不能升级"}
                  onClick={() => {
                    if (!purchaseUrl) return;
                    // 充值后回到当前页（带 `?billing=1` 的刷新由 BillingReturn 接手）。
                    window.open(
                      withBillingReturn(purchaseUrl, currentPageHref()),
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  升级套餐
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
